import * as React from 'react';
import '../commonStyles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { currencyUtils, dateFormatter, localStorageHelper } from 'utils';
import GamesBarChart from 'Containers/Charts/GamesCharts/GamesBarChart';
import { IGameData } from 'redux/reducers/backoffice/games-report-reducer';
import { getGamesReport } from 'redux/actions/backoffice/games-report-actions';
import {
    getGames,
    getGamesTotalLine,
    getRawGamesData,
} from 'redux/selectors/backoffice/games-report-selectors';
import { getServerError } from 'redux/selectors/backoffice/error-selectors';
import { useCallback, useEffect, useState } from 'react';
import { ContentComponent } from '../../../Components/ContentComponent/ContentComponent';
import { Link } from 'react-router-dom';
import { Context } from '../../../App';
import TableHeaderWrapper from '../../../Components/Table/TableHeaderWrapper';
import { HeaderTitleComponent } from '../../../Components/ContentComponent/HeaderTitleComponent';
import TotalLineComponent from '../../../Components/TotalLineComponent/TotalLineComponent';
import { gamesReportData } from '../columnsData';
import { ErrorComponent } from '../../Errors/ErrorComponent';
import { Empty, Table } from 'antd';
import { Loader } from '../../../Components/Loader/Loader';
import { GamesColumns } from './GamesColumns';
import { useTranslation } from 'react-i18next';
import { getDateOfLastRefresh } from '../../../redux/selectors/user-selectors';

const GamesReportComponent: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const data: IGameData[] = useSelector(getGames);
    const rawData: IGameData[] = useSelector(getRawGamesData);
    const totalLine: any = useSelector(getGamesTotalLine);
    const dateOfLastRefresh: Date = useSelector(getDateOfLastRefresh);
    const error: string = useSelector(getServerError);
    const activeCurrencyCode = currencyUtils.getActiveCurrencyCode();
    const isFiltered =
        localStorageHelper.getChainedValue('user.reports.filters.currencies')?.length > 0;
    const storedRoundTypes = localStorageHelper.getChainedValue('user.reports.roundTypes');
    const [selectedRoundTypes, setSelectedRoundTypes] = useState<string[]>(
        storedRoundTypes || ['regular', 'buy', 'free-round', 'awarded-feature'],
    );
    const currenciesBreakdown = localStorageHelper.getChainedValue(
        'user.reports.filters.currenciesBreakdown',
    );
    const columns = GamesColumns(activeCurrencyCode, isFiltered);
    const initialDates = localStorageHelper.getChainedValue('user.reports.dates');
    const [loading, setLoading] = useState(true);
    const [tableHeight, setTableHeight] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [fromDate, setFromDate] = useState<Date>(
        initialDates?.fromDate || dateFormatter.getDefaultDate(),
    );
    const [toDate, setToDate] = useState<Date>(
        initialDates?.toDate || dateFormatter.getDefaultDate(false),
    );
    const totalWidth = columns.reduce((total, col) => total + (col.width || 0), 0);
    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight * 0.6 - 194;
        setTableHeight(height);
    }, []);

    const getReportData = useCallback(() => {
        dispatch(getGamesReport({ fromDate, toDate, roundTypes: selectedRoundTypes }));
    }, [fromDate, toDate, dispatch, selectedRoundTypes]);

    const handlePageSizeChange = (current: any, size: any) => {
        setPageSize(size);
    };

    const parentReportCallback = (dates: { fromDate: Date; toDate: Date }) => {
        setFromDate(dates.fromDate);
        setToDate(dates.toDate);
    };

    const reportData = data ? (data.length ? data : []) : null;

    useEffect(() => {
        getReportData();
    }, [getReportData, fromDate, toDate]);

    useEffect(() => {
        setLoading(false);
    }, [data, error]);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const chart =
        !loading &&
        data &&
        !localStorageHelper.getChainedValue('user.reports.filters.currenciesBreakdown') ? (
            <GamesBarChart data={rawData} />
        ) : null;

    return (
        <ContentComponent
            customBreadcrumbs={<Link to={`/casino/reports`}>{t('reports')}</Link>}
            header={
                <Context.Consumer>
                    {(context) => (
                        <TableHeaderWrapper
                            selectedRoundTypes={selectedRoundTypes}
                            setSelectedRoundTypes={setSelectedRoundTypes}
                            titleComponent={<HeaderTitleComponent title={t('games_performance')} />}
                            parentReportCallback={parentReportCallback}
                            getReport={getReportData}
                            fromDate={fromDate}
                            toDate={toDate}
                            reportKey="games"
                            context={context}
                            dateOfLastRefresh={dateOfLastRefresh}
                        />
                    )}
                </Context.Consumer>
            }
            preHeader={
                reportData && reportData.length && !error ? (
                    <TotalLineComponent data={totalLine} columns={gamesReportData} />
                ) : undefined
            }
            innerContent={
                error ? (
                    <ErrorComponent error={error} />
                ) : reportData ? (
                    reportData.length ? (
                        <>
                            {chart}
                            <div className="casino_performance-table-container">
                                <div className={'casino_performance-table-title'}>
                                    <div className="content-wrapper">
                                        {currenciesBreakdown && 'Data broken down by currency'}
                                    </div>
                                </div>
                                <Table
                                    style={{
                                        minWidth: '100%',
                                        paddingBottom: '16px',
                                    }}
                                    className={'casino_performance-table'}
                                    dataSource={reportData}
                                    scroll={{ x: totalWidth, y: tableHeight }}
                                    columns={columns}
                                    pagination={{
                                        pageSize,
                                        position: ['topRight'],
                                        pageSizeOptions: [10, 20, 50, 100],
                                        onShowSizeChange: handlePageSizeChange,
                                    }}
                                    size="middle"
                                    rowKey={(record) =>
                                        `${record.roundType.toString()}-${record.fromDate.toString()}-${record.toDate.toString()}-${record.gameId.toString()}-${record.currencyId.toString()}`
                                    }
                                    footer={() => <></>}
                                />
                            </div>
                        </>
                    ) : (
                        <Empty description={t('no_data')} />
                    )
                ) : (
                    <Loader style={{ height: '90vh' }} />
                )
            }
        />
    );
};

export default GamesReportComponent;
